// app/src/lib/reports/tileSignals.js — at-a-glance indicators for the Reports
// menu tiles (green/amber/red dots instead of static captions).
//
// Volatility rule: no trend ever compares a window containing a partial
// settlement week. Deposit-based sales compare the last 4 COMPLETE Mon–Sun
// weeks against the prior 4 — both windows hold the same weekday mix and the
// same number of weekly delivery payouts (Tuesdays) and T+2 card cycles, so
// deposit-timing noise cancels instead of reading as a mid-week "drop".
//
// Pure functions first (unit-tested in test/tileSignals.test.js), then
// computeReportSignals() — the single async pass the Reports menu calls.

import { db } from '../db.js';
import { requireUserId } from '../procurement/util.js';
import { isoToday, addDays, mondayOf, daysBetween } from '../cashflow/util.js';
import * as accounts from '../cashflow/accounts.js';
import * as spend from '../cashflow/spendCategory.js';
import * as rentLib from '../cashflow/rent.js';
import * as insuranceLib from '../cashflow/insurance.js';
import * as payrollLib from '../cashflow/payroll.js';
import * as expenseCategories from '../cashflow/expenseKeywords.js';
import * as capexLib from '../cashflow/capex.js';
import { build as buildForecast } from '../cashflow/forecast.js';

const STEADY_PCT = 3;          // ± band that reads as "steady" rather than a move
const RENT_STEP_PCT = 2;       // rent wobble below this is noise, not an increase
const RUN_RATE_AMBER_PCT = 15; // supplies run-rate over the 3-mo avg by more → amber
const CADENCE_SLACK = 1.5;     // payroll gap beyond usual × this → checks overdue
const RUN_SPAN_DAYS = 3;       // check clearing dates within this of each other = one payroll run
const LAG_GRACE_DAYS = 2;      // projected check this late = feed lag; later = the run slipped
const GAP_RED_WEEKS = 4;       // forecast gap within this many weeks → red

// ─── Pure signal functions ───────────────────────────────────────────────────

/**
 * Deposit-dated sales: last `weeks` complete Mon–Sun weeks vs the prior
 * `weeks`. The in-progress week is excluded entirely.
 * @param {Array<{date:string, netSalesCents:number}>} entries
 * @returns {{ curCents:number, prevCents:number, pct:number, tone:'up'|'down'|'steady' }|null}
 */
export function weeklySalesTrend(entries, todayIso, { weeks = 4 } = {}) {
  const thisMon = mondayOf(todayIso);
  const curFrom = addDays(thisMon, -7 * weeks);
  const prevFrom = addDays(thisMon, -14 * weeks);
  let curCents = 0, prevCents = 0;
  for (const e of entries || []) {
    const d = e.date || '';
    if (d >= curFrom && d < thisMon) curCents += e.netSalesCents || 0;
    else if (d >= prevFrom && d < curFrom) prevCents += e.netSalesCents || 0;
  }
  if (prevCents <= 0) return null; // no full prior window → no fair comparison
  const pct = ((curCents - prevCents) / prevCents) * 100;
  const tone = Math.abs(pct) <= STEADY_PCT ? 'steady' : pct > 0 ? 'up' : 'down';
  return { curCents, prevCents, pct, tone };
}

/**
 * Rent changes at most once a year, so the signal is the most recent step-up
 * (> RENT_STEP_PCT) in the BASE rent payment that then HOLDS — every later
 * paid month stays at the new level (2% slack). A one-month spike (a CAM
 * true-up, a double payment) is not an increase. Callers pass the largest
 * single rent payment per month, not the month's sum, for the same reason.
 * @param {Array<{ym:string, rentCents:number}>} months ascending
 * @returns {{ kind:'increase', ym:string, pct:number, fromCents:number, toCents:number }
 *          |{ kind:'none' }|null}
 */
export function rentIncreaseSignal(months, todayYm) {
  const cutoff = shiftYm(todayYm, -12);
  const paid = (months || []).filter((m) => (m.rentCents || 0) > 0 && m.ym <= todayYm).sort((a, b) => a.ym.localeCompare(b.ym));
  if (paid.length < 2) return null;
  let latest = null;
  for (let i = 1; i < paid.length; i += 1) {
    const prev = paid[i - 1], cur = paid[i];
    if (cur.ym < cutoff) continue;
    const pct = ((cur.rentCents - prev.rentCents) / prev.rentCents) * 100;
    if (pct <= RENT_STEP_PCT) continue;
    const holds = paid.slice(i + 1).every((m) => m.rentCents >= cur.rentCents * 0.98);
    if (holds) latest = { kind: 'increase', ym: cur.ym, pct, fromCents: prev.rentCents, toCents: cur.rentCents };
  }
  return latest || { kind: 'none' };
}

/**
 * Utility bills swing month to month — report the % change between the two
 * most recent COMPLETE months (the current month's bills haven't all landed).
 * @param {Array<{ym:string, utilCents:number}>} months ascending
 * @returns {{ ym:string, prevYm:string, pct:number, tone:'up'|'down'|'steady' }|null}
 */
export function utilitiesMoMSignal(months, todayYm) {
  const complete = (months || []).filter((m) => m.ym < todayYm && (m.utilCents || 0) > 0).sort((a, b) => a.ym.localeCompare(b.ym));
  if (complete.length < 2) return null;
  const cur = complete[complete.length - 1];
  const prev = complete[complete.length - 2];
  if (shiftYm(cur.ym, -1) !== prev.ym) return null; // non-adjacent → stale feed, don't fake a MoM
  const pct = ((cur.utilCents - prev.utilCents) / prev.utilCents) * 100;
  const tone = Math.abs(pct) <= STEADY_PCT ? 'steady' : pct > 0 ? 'up' : 'down';
  return { ym: cur.ym, prevYm: prev.ym, pct, tone };
}

/**
 * Payroll checks land on a rhythm (usually biweekly) on a fixed weekday —
 * only bank holidays push a check a day. Bank-feed dates are CLEARING dates,
 * so one payroll run can span several of them (each employee deposits their
 * check when they get to it): cluster dates within RUN_SPAN_DAYS into a run
 * anchored at its FIRST clearing date, so in-run spread never pollutes the
 * cadence gaps or drags the projection anchor. Project the next run (last
 * anchor + usual gap, snapped back to the usual weekday so a holiday-shifted
 * run doesn't skew every later projection). A projection more than
 * LAG_GRACE_DAYS in the past means the run slipped — roll to the following
 * cycle instead of pinning the reminder for a week.
 *
 * The reminder the owner actually needs is the PREP day: the pay period ends
 * Thursday COB, the checks are keyed in Friday morning, and they land the
 * following Monday (clearing Mon-Tue). So the countdown targets the last
 * FRIDAY strictly before the projected run ('prep' phase: green ≥3 days out,
 * amber under 3, red tomorrow/today); once prep day has passed, it flips to a
 * quiet 'landing' phase counting to the run itself. Once the LAST SEEN run is
 * further back than CADENCE_SLACK × the usual gap, the countdown no longer
 * means anything — flag the gap instead (overdue). Needs ≥3 runs of history.
 * @param {string[]} checkDates ISO dates, any order
 * @returns {{ tone:'green'|'amber'|'red', phase:'prep'|'landing', daysUntil:number,
 *             prepDate:string, nextDate:string, medianGapDays:number,
 *             daysSince:number, overdue:boolean }|null}
 */
export function payrollCadenceSignal(checkDates, todayIso) {
  const dates = [...new Set(checkDates || [])].sort(); // ascending, de-duped
  if (dates.length < 3) return null;
  const runs = [];
  for (const d of dates) {
    const cur = runs[runs.length - 1];
    if (cur && daysBetween(d, cur.end) <= RUN_SPAN_DAYS) cur.end = d;
    else runs.push({ start: d, end: d });
  }
  const anchors = runs.map((r) => r.start);
  if (anchors.length < 3) return null;
  const gaps = [];
  for (let i = 1; i < anchors.length; i += 1) gaps.push(daysBetween(anchors[i], anchors[i - 1])); // daysBetween(a,b) = a − b
  gaps.sort((a, b) => a - b);
  const medianGapDays = gaps[Math.floor(gaps.length / 2)];
  const daysSince = daysBetween(todayIso, anchors[anchors.length - 1]);
  // Payroll weekday = the mode of run starts. Snap a projection to the
  // nearest such weekday: a run that started Tuesday because Monday was a
  // holiday still projects the NEXT one onto Monday.
  const weekdayOf = (iso) => new Date(iso + 'T00:00:00Z').getUTCDay();
  const counts = new Array(7).fill(0);
  for (const d of anchors) counts[weekdayOf(d)] += 1;
  const payday = counts.indexOf(Math.max(...counts));
  const snap = (iso) => {
    let shift = (payday - weekdayOf(iso) + 7) % 7;
    if (shift > 3) shift -= 7; // nearest occurrence, not always forward
    return addDays(iso, shift);
  };
  let nextDate = snap(addDays(anchors[anchors.length - 1], medianGapDays));
  // Projection slightly past = checks still clearing (reads "due today");
  // further past = that run slipped or the feed missed it — count down to the
  // following cycle instead. (Inter-run gaps exceed RUN_SPAN_DAYS and snap
  // shifts at most 3 days, so each step advances ≥1 day and terminates.)
  while (daysBetween(todayIso, nextDate) > LAG_GRACE_DAYS) {
    nextDate = snap(addDays(nextDate, medianGapDays));
  }
  // Prep day = the last Friday strictly before the projected run (checks are
  // written Friday morning; the run lands/clears early the following week).
  const FRIDAY = 5;
  const back = ((weekdayOf(nextDate) - FRIDAY) + 7) % 7 || 7;
  const prepDate = addDays(nextDate, -back);
  const daysToPrep = daysBetween(prepDate, todayIso);
  const phase = daysToPrep >= 0 ? 'prep' : 'landing';
  const daysUntil = phase === 'prep' ? daysToPrep : Math.max(0, daysBetween(nextDate, todayIso));
  const overdue = daysSince > Math.max(7, medianGapDays * CADENCE_SLACK);
  const tone = overdue ? 'amber'
    : phase === 'landing' ? 'green' // checks are written and on their way — nothing to do
    : daysUntil <= 1 ? 'red' : daysUntil < 3 ? 'amber' : 'green';
  return { tone, phase, daysUntil, prepDate, nextDate, medianGapDays, daysSince, overdue };
}

/**
 * Last complete month vs the average of the 3 months before it (needs ≥2 of
 * those with activity). Over by more than RUN_RATE_AMBER_PCT → amber; spending
 * at or under the run rate is green.
 * @param {Array<{ym:string, total:number}>} monthly ascending
 */
export function runRateSignal(monthly, todayYm) {
  const byYm = new Map((monthly || []).map((m) => [m.ym, m.total || 0]));
  const lastYm = shiftYm(todayYm, -1);
  const last = byYm.get(lastYm) || 0;
  const prior = [shiftYm(lastYm, -1), shiftYm(lastYm, -2), shiftYm(lastYm, -3)]
    .map((ym) => byYm.get(ym) || 0).filter((v) => v > 0);
  if (prior.length < 2) return null;
  const avg = prior.reduce((s, v) => s + v, 0) / prior.length;
  if (avg <= 0 && last <= 0) return null;
  const pct = ((last - avg) / avg) * 100;
  return { ym: lastYm, pct, tone: pct > RUN_RATE_AMBER_PCT ? 'amber' : 'green' };
}

/**
 * 13-week forecast health: red when the first negative week is inside
 * GAP_RED_WEEKS, amber when further out, green when it never goes negative.
 * @param {Array<{closingCents:number}>} weeks
 * @returns {{ tone:'green'|'amber'|'red', gapWeek:number|null }|null}
 */
export function forecastHealthSignal(weeks) {
  if (!Array.isArray(weeks) || weeks.length === 0) return null;
  const idx = weeks.findIndex((w) => (w.closingCents || 0) < 0);
  if (idx === -1) return { tone: 'green', gapWeek: null };
  return { tone: idx < GAP_RED_WEEKS ? 'red' : 'amber', gapWeek: idx + 1 };
}

/** 'YYYY-MM' shifted by n months. */
function shiftYm(ym, n) {
  const [y, m] = ym.split('-').map(Number);
  const d = new Date(Date.UTC(y, m - 1 + n, 1));
  return d.toISOString().slice(0, 7);
}

// ─── Orchestrator — one async pass for the Reports menu ─────────────────────

/**
 * Compute every tile signal in one pass over shared data pulls. Each block is
 * individually fail-soft (a broken source nulls its signal, never the menu).
 * @returns {Promise<{
 *   sales: ReturnType<typeof weeklySalesTrend>,
 *   cash: ReturnType<typeof forecastHealthSignal>,
 *   payroll: ReturnType<typeof payrollCadenceSignal>,
 *   supplies: { signal: ReturnType<typeof runRateSignal>, hasFeed: boolean },
 *   rent: { increase: ReturnType<typeof rentIncreaseSignal>, utilities: ReturnType<typeof utilitiesMoMSignal> },
 *   insurance: { increase: object|null, hasPayments: boolean }|null,
 *   capex: { ytdCents:number, lastYearCents:number, thisYear:number, priorYear:number, newThisMonth:boolean }|null,
 * }>}
 */
export async function computeReportSignals() {
  const userId = await requireUserId();
  const today = isoToday();
  const todayYm = today.slice(0, 7);
  const soft = (p) => p.catch((e) => { console.warn('[tileSignals]', e); return null; });

  // Shared pulls. 425d of transactions covers the rent YoY-adjacent windows,
  // insurance installment pairs, payroll cadence, and supplies run rate.
  const txCutoff = addDays(today, -425);
  const [salesEntries, txsAll, creditSet, explicitCtx, forecast, capexSum] = await Promise.all([
    soft(db.salesEntries.where('[userId+date]').between([userId, addDays(today, -9 * 7)], [userId, today], true, true).toArray()),
    soft(db.plaidTransactions.where('[userId+date]').between([userId, txCutoff], [userId, today], true, true).toArray()),
    soft(accounts.creditAccountIds(userId)),
    soft(spend.loadExplicitContext(userId)),
    soft(buildForecast()),
    soft(capexLib.summarize()),
  ]);

  const out = { sales: null, cash: null, payroll: null, supplies: { signal: null, hasFeed: false }, rent: { increase: null, utilities: null }, insurance: null, capex: null };

  if (salesEntries) out.sales = weeklySalesTrend(salesEntries, today);
  if (forecast) out.cash = forecastHealthSignal(forecast.weeks);

  if (txsAll && creditSet) {
    // Same two feeds the report screens read: card-inclusive (stamped with the
    // operator's explicit Spending categories) for rent/insurance; depository-
    // only for payroll checks. Keeping the bases identical means a tile dot
    // never disagrees with the screen behind it.
    let cardIncl = txsAll.filter((t) => !accounts.isCardPayment(t));
    if (explicitCtx) {
      try { cardIncl = spend.stampCardCategories(cardIncl, creditSet, explicitCtx); }
      catch (e) { console.warn('[tileSignals] spend stamp skipped', e); }
    }
    const depository = accounts.withoutCreditTxs(txsAll, creditSet);

    // Rent & utilities — increases only (owner request 2026-07-12).
    try {
      const [rentKw, expKw] = await Promise.all([soft(rentLib.getKeywords()), soft(expenseCategories.getKeywords())]);
      if (rentKw) {
        const sum = rentLib.summarize(cardIncl, rentKw, expKw);
        const months = sum.monthly.map((m) => {
          // rentCents = the largest single rent-bucket payment (the base rent
          // check). Summing would turn a double-payment month or a one-off
          // CAM charge into a phantom "rent increase". Utilities stay summed —
          // the MoM swing across all utility bills is exactly the signal.
          let utilCents = 0, rentCents = 0;
          for (const r of m.rows) {
            if (r._bucket === 'utility') utilCents += r.amountCents || 0;
            else if (r._bucket === 'rent') rentCents = Math.max(rentCents, r.amountCents || 0);
            // 'mro' rows are lumpy one-off repairs — excluded from both
            // signals (a big repair is neither a rent step-up nor a utility swing).
          }
          return { ym: m.ym, utilCents, rentCents };
        });
        out.rent = { increase: rentIncreaseSignal(months, todayYm), utilities: utilitiesMoMSignal(months, todayYm) };
      }
    } catch (e) { console.warn('[tileSignals] rent failed', e); }

    try {
      const insKw = await soft(insuranceLib.getKeywords());
      if (insKw) {
        const sum = insuranceLib.summarize(cardIncl, insKw);
        out.insurance = { increase: sum.latestIncrease || null, hasPayments: sum.payments.length > 0 };
      }
    } catch (e) { console.warn('[tileSignals] insurance failed', e); }

    try {
      const ranges = await soft(payrollLib.getCheckRanges());
      if (ranges && ranges.length > 0) {
        const { checks } = payrollLib.summarizeChecks(depository, ranges);
        out.payroll = payrollCadenceSignal(checks.map((c) => c.date), today);
      }
    } catch (e) { console.warn('[tileSignals] payroll failed', e); }

    try {
      out.supplies.hasFeed = creditSet.size > 0;
      if (out.supplies.hasFeed && explicitCtx) {
        const byYm = new Map();
        for (const t of txsAll) {
          if (!creditSet.has(t.plaidAccountId) || t.pending) continue;
          if (spend.explicitCategory(t, explicitCtx) !== 'supplies') continue;
          const ym = (t.date || '').slice(0, 7);
          byYm.set(ym, (byYm.get(ym) || 0) + (t.amountCents || 0));
        }
        out.supplies.signal = runRateSignal([...byYm].map(([ym, total]) => ({ ym, total })), todayYm);
      }
    } catch (e) { console.warn('[tileSignals] supplies failed', e); }
  }

  if (capexSum) {
    out.capex = {
      ytdCents: capexSum.ytdCents, lastYearCents: capexSum.lastYearCents,
      thisYear: capexSum.thisYear, priorYear: capexSum.priorYear,
      newThisMonth: (capexSum.items || []).some((i) => (i.date || '').slice(0, 7) === todayYm),
    };
  }
  return out;
}
